/**
 * useControlWarning — reużywalny hook UX dla mechanizmu "kontrola dokumentu"
 * (tenant-owned procedura kartoteki, patrz backend `apps/api/lib/record-hooks/`).
 *
 * Kontrakt HTTP (backend → frontend), wspólny dla wszystkich klas dokumentów:
 *   - `CONTROL_ERROR` (400)        → blokada twarda, toast z treścią od tenanta.
 *   - `CONTROL_WARNING` (409)      → miękkie ostrzeżenie: pokazujemy treść od tenanta
 *     w `<ConfirmDialog>`; potwierdzenie ponawia ten sam zapis z `warningsAccepted:true`
 *     domieszanym do payloadu; anulowanie zostawia formularz nietknięty.
 *   - `CONTROL_HOOK_FAILED` (500)  → błąd wykonania hooka po stronie tenanta (fail-closed),
 *     toast z generycznym komunikatem i18n — SZCZEGÓŁY tylko w logu serwera, nigdy w UI.
 *
 * Użycie w mutacji strony (np. `PurchaseInvoiceDetail.tsx`):
 *
 *   const controlWarning = useControlWarning<PurchaseInvoiceFormData>({
 *     mutate: (payload) => saveMutation.mutate(payload),
 *   });
 *
 *   const saveMutation = useMutation({
 *     mutationFn: async (data: PurchaseInvoiceFormData) => { ...cleaned.warningsAccepted = data.warningsAccepted... },
 *     onError: (err) => {
 *       if (controlWarning.handleError(err, form)) return; // CONTROL_* obsłużone
 *       // ...pozostałe gałęzie (INVOICE_NUMBER_TAKEN, VENDOR_NOT_FOUND, generic)...
 *     },
 *   });
 *
 *   <>{controlWarning.dialog}</>
 *
 * Hook jest celowo bezstanowy względem kształtu payloadu — parametryzowany generykiem
 * `TPayload`, więc każda strona dokumentowa (5 kolejnych klas po fakturach zakupu) może
 * go użyć bez zmian, o ile jej payload da się rozszerzyć o `warningsAccepted?: boolean`.
 *
 * Opcjonalne `titleKey` / `confirmLabelKey` pozwalają nadpisać klucze i18n dialogu —
 * domyślnie flow SAVE („Zapisz mimo to"), ale ten sam hook obsłuży też flow DELETE
 * (np. „Usuń mimo to") przekazując własne klucze:
 *
 *   const deleteControlWarning = useControlWarning<{ id: string }>({
 *     mutate: (payload) => deleteMutation.mutate(payload),
 *     titleKey: 'tenant.documents.controlWarning.deleteTitle',
 *     confirmLabelKey: 'tenant.documents.controlWarning.deleteConfirmLabel',
 *   });
 *
 * Opcjonalne `errorKey` zmienia tekst toastu `CONTROL_ERROR` używany, gdy procedura nie podała
 * własnej treści (domyślnie tekst o zapisie dokumentu) — dla wołających spoza dokumentów, np.
 * okna decyzji procesu (`tenant.processes.actions.controlError`).
 *
 * Opcjonalne `onCancel` jest wołane, gdy user zamknie dialog ostrzeżenia bez potwierdzenia —
 * np. żeby zapomnieć okno decyzji procesu czekające na wynik zapisu (`useSaveBeforeDecision`).
 */

import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';
import { extractErrorCode } from '@/lib/errors';

/** Payload dowolnej mutacji zapisu dokumentu, rozszerzalny o flagę akceptacji ostrzeżenia. */
export type WithWarningsAccepted<TPayload> = TPayload & { warningsAccepted?: boolean };

/**
 * Wyciąga `message` z ciała odpowiedzi błędu axios (`{error, message}` — patrz
 * kontrakt backendu). Analogiczne narrowing do `extractErrorCode` w `lib/errors.ts`,
 * bez `any` — `data` jest `unknown` do czasu potwierdzenia kształtu.
 */
function extractErrorMessage(err: unknown): string | undefined {
  if (!isAxiosError(err)) return undefined;
  const data: unknown = err.response?.data;
  if (data && typeof data === 'object' && 'message' in data) {
    const message = (data as { message?: unknown }).message;
    if (typeof message === 'string') return message;
  }
  return undefined;
}

export interface UseControlWarningOptions<TPayload extends object> {
  /**
   * Funkcja ponawiająca zapis (zwykle `saveMutation.mutate`). Wywoływana z ORYGINALNYM
   * payloadem przekazanym do `handleError`, domieszanym o `warningsAccepted: true`.
   */
  mutate: (payload: WithWarningsAccepted<TPayload>) => void;
  /**
   * Nadpisanie klucza i18n tytułu dialogu (i fallbacku wiadomości ostrzeżenia, gdy
   * backend nie zwróci treści). Domyślnie `tenant.documents.controlWarning.title`
   * (flow SAVE). Dla flow DELETE użyj osobnego klucza (np. „Usuń mimo to").
   */
  titleKey?: string;
  /**
   * Nadpisanie klucza i18n etykiety przycisku potwierdzenia. Domyślnie
   * `tenant.documents.controlWarning.confirmLabel` (flow SAVE, „Zapisz mimo to").
   * Dla flow DELETE użyj osobnego klucza (np. „Usuń mimo to").
   */
  confirmLabelKey?: string;
  /**
   * Nadpisanie klucza i18n tekstu toastu `CONTROL_ERROR` (blokada twarda), używanego, gdy
   * procedura kontrolna nie podała własnej treści. Domyślnie `tenant.documents.controlError`
   * („Zapis został zablokowany…" — flow zapisu dokumentu). Wołający spoza dokumentów (np. okno
   * decyzji procesu) przekazuje własny tekst, żeby nie pokazać komunikatu o zapisie.
   */
  errorKey?: string;
  /**
   * Nadpisanie klucza i18n TREŚCI dialogu ostrzeżenia, gdy procedura kontrolna nie podała własnej
   * treści. Domyślnie ten sam klucz co tytuł (`titleKey`). Wołający, którego tytuł jest pytaniem
   * (np. okno decyzji procesu: „Kontynuować mimo ostrzeżenia?"), przekazuje osobne zdanie — inaczej
   * dialog pokazałby dwa razy ten sam tekst.
   */
  warningFallbackKey?: string;
  /**
   * Wywoływane, gdy user ANULUJE dialog ostrzeżenia („Anuluj", X, Esc, tło) — zapis nie zostaje
   * ponowiony. NIE jest wołane przy potwierdzeniu ani przy błędach `CONTROL_ERROR` /
   * `CONTROL_HOOK_FAILED`. Pozwala wołającemu zapomnieć akcję czekającą na wynik zapisu
   * (np. okno decyzji procesu otwierane po zapisie — `useSaveBeforeDecision`).
   */
  onCancel?: () => void;
}

export interface UseControlWarningResult<TPayload extends object> {
  /**
   * Interceptor do wywołania na początku `onError` mutacji zapisu. Zwraca `true`,
   * gdy kod błędu to jeden z `CONTROL_ERROR` / `CONTROL_WARNING` / `CONTROL_HOOK_FAILED`
   * (obsłużone — toast lub dialog już pokazany), inaczej `false` — strona kontynuuje
   * własne gałęzie (np. `INVOICE_NUMBER_TAKEN`).
   */
  handleError: (err: unknown, payload: TPayload) => boolean;
  /** Zrenderowany `<ConfirmDialog>` ostrzeżenia kontroli — osadź go w JSX strony. */
  dialog: ReactNode;
}

export function useControlWarning<TPayload extends object>({
  mutate,
  titleKey = 'tenant.documents.controlWarning.title',
  confirmLabelKey = 'tenant.documents.controlWarning.confirmLabel',
  errorKey = 'tenant.documents.controlError',
  warningFallbackKey,
  onCancel,
}: UseControlWarningOptions<TPayload>): UseControlWarningResult<TPayload> {
  const { t } = useTranslation();
  const toast = useToast();

  // Stan miękkiego ostrzeżenia: treść od tenanta + payload, który ma zostać
  // ponowiony po potwierdzeniu. `null` = dialog zamknięty.
  const [pendingWarning, setPendingWarning] = useState<{
    message: string;
    payload: TPayload;
  } | null>(null);

  const handleError = useCallback((err: unknown, payload: TPayload): boolean => {
    const code = extractErrorCode(err);
    if (code === 'CONTROL_WARNING') {
      const message = extractErrorMessage(err);
      setPendingWarning({
        message: message ?? t(warningFallbackKey ?? titleKey),
        payload,
      });
      return true;
    }
    if (code === 'CONTROL_ERROR') {
      const message = extractErrorMessage(err);
      toast.show({
        variant: 'error',
        title: message ?? t(errorKey),
      });
      return true;
    }
    if (code === 'CONTROL_HOOK_FAILED') {
      toast.show({
        variant: 'error',
        title: t('tenant.documents.controlHookFailed'),
      });
      return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- toast/t/titleKey/errorKey stabilne (context/i18next/props), nie wymagają deklarowania
  }, []);

  const handleConfirm = useCallback(() => {
    if (!pendingWarning) return;
    const { payload } = pendingWarning;
    setPendingWarning(null);
    mutate({ ...payload, warningsAccepted: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutate przekazywany przez wołającego per-render, nie chcemy re-tworzyć callbacku na każdą zmianę referencji
  }, [pendingWarning]);

  const handleClose = useCallback(() => {
    setPendingWarning(null);
    onCancel?.();
  }, [onCancel]);

  const dialog = (
    <ConfirmDialog
      open={pendingWarning !== null}
      variant="primary"
      title={t(titleKey)}
      message={pendingWarning?.message ?? ''}
      confirmLabel={t(confirmLabelKey)}
      onConfirm={handleConfirm}
      onClose={handleClose}
    />
  );

  return { handleError, dialog };
}
